import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Calendar,
  Clock,
  ArrowLeft,
  MessageCircle,
  ChevronRight,
} from "lucide-react";
import { KENTRO } from "@/lib/constants";

const SITE = "https://kentro.com.br";
const URL = `${SITE}/medindo-o-impacto-emocional-das-reclamacoes-dos-clientes-aprimorando-processos-para-o-sucesso`;
const TITLE =
  "Medindo o impacto emocional das reclamações dos clientes: aprimorando processos para o sucesso";
const DESCRIPTION =
  "Reclamações carregam emoção — e essa emoção tem impacto direto na retenção, no LTV e na cultura do time. Veja como medir, interpretar e transformar reclamações em melhoria contínua de processos.";
const KEYWORD = "impacto emocional reclamações de clientes";
const CATEGORY = "Experiência do Cliente";
const AUTHOR = "Equipe Kentro";
const DATE_ISO = "2024-10-08";
const READING_MIN = 9;

const FAQ = [
  {
    q: "O que é impacto emocional de uma reclamação?",
    a: "É a carga afetiva que a experiência negativa gera no cliente — frustração, raiva, decepção, ansiedade — e o efeito que essa carga produz no comportamento dele: pedir reembolso, falar mal nas redes, cancelar o contrato ou simplesmente nunca mais voltar. Reclamação não é só conteúdo, é emoção.",
  },
  {
    q: "Como medir o impacto emocional sem virar pesquisa acadêmica?",
    a: "Combine três sinais simples: sentimento da conversa (positivo/neutro/negativo com IA), esforço percebido (CES de 1 a 7) e intenção futura (NPS pós-reclamação). Cruze com o motivo do contato e você tem um mapa prático de onde a operação está machucando o cliente.",
  },
  {
    q: "Reclamação resolvida rápido apaga o impacto emocional?",
    a: "Reduz, mas não apaga. Estudos de CX mostram que clientes que tiveram um problema bem resolvido ficam mais leais que clientes que nunca tiveram problema — desde que a resolução tenha sido empática, transparente e sem fazer o cliente repetir o caso. Velocidade sem empatia não funciona.",
  },
  {
    q: "Como transformar reclamação em melhoria de processo?",
    a: "Categorize toda reclamação por causa raiz (não por sintoma), priorize pelo volume × impacto emocional, leve o top 3 para um ritual semanal com produto/operação e feche o ciclo avisando o cliente que reclamou que a mudança aconteceu. Isso transforma detrator em embaixador.",
  },
];

export const Route = createFileRoute(
  "/medindo-o-impacto-emocional-das-reclamacoes-dos-clientes-aprimorando-processos-para-o-sucesso",
)({
  head: () => {
    const articleLd = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: TITLE,
      description: DESCRIPTION,
      image: [`${SITE}/og-image.png`],
      datePublished: DATE_ISO,
      dateModified: DATE_ISO,
      inLanguage: "pt-BR",
      author: { "@type": "Organization", name: AUTHOR, url: SITE },
      publisher: {
        "@type": "Organization",
        name: "Kentro Sistemas",
        logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
      },
      mainEntityOfPage: URL,
      keywords: KEYWORD,
      articleSection: CATEGORY,
    };
    const breadcrumbLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Início", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/blog` },
        { "@type": "ListItem", position: 3, name: TITLE, item: URL },
      ],
    };
    const faqLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    return {
      meta: [
        { title: `${TITLE} | Kentro` },
        { name: "description", content: DESCRIPTION },
        { name: "keywords", content: KEYWORD },
        { property: "og:type", content: "article" },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: URL },
        { property: "og:image", content: `${SITE}/og-image.png` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
        { name: "twitter:image", content: `${SITE}/og-image.png` },
        { name: "article:published_time", content: DATE_ISO },
        { name: "article:section", content: CATEGORY },
      ],
      links: [{ rel: "canonical", href: URL }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(articleLd) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumbLd) },
        { type: "application/ld+json", children: JSON.stringify(faqLd) },
      ],
    };
  },
  component: ArticlePage,
});

function ArticlePage() {
  const formattedDate = new Date(DATE_ISO).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <article className="mx-auto max-w-3xl px-4 pt-24 pb-16 sm:px-6 lg:px-8">
      <nav
        aria-label="Breadcrumb"
        className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground"
      >
        <Link to="/" className="hover:text-foreground">
          Início
        </Link>
        <ChevronRight className="size-3" />
        <Link to="/blog" className="hover:text-foreground">
          Blog
        </Link>
        <ChevronRight className="size-3" />
        <span className="text-foreground/80">{CATEGORY}</span>
      </nav>

      <div className="mt-6 rounded-2xl border border-border bg-card/60 p-6 shadow-sm backdrop-blur-sm md:p-10">
        <header>
          <span className="inline-flex rounded-full border border-border bg-background/60 px-2.5 py-1 text-xs text-muted-foreground">
            {CATEGORY}
          </span>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-5xl">
            {TITLE}
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">{DESCRIPTION}</p>
          <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="size-4" /> {formattedDate}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="size-4" /> {READING_MIN} min de leitura
            </span>
            <span>Por {AUTHOR}</span>
          </div>
        </header>

        <div className="prose mt-10 max-w-none text-foreground prose-headings:text-foreground prose-p:text-foreground/90 prose-strong:text-foreground prose-li:text-foreground/90 prose-a:text-primary-glow hover:prose-a:underline prose-blockquote:text-foreground/80 prose-blockquote:border-l-primary-glow prose-code:text-foreground prose-hr:border-border dark:prose-invert">
          <p>
            Toda reclamação chega com duas camadas: o <strong>fato</strong> (o
            pedido atrasou, o boleto veio errado, o app travou) e a{" "}
            <strong>emoção</strong> (eu me senti enganado, ignorado, ridículo).
            A maioria das empresas só mede a primeira camada — categoriza por
            motivo, fecha o ticket e segue a vida. O problema é que quem decide
            ficar ou cancelar é a segunda camada. Sem medir o{" "}
            <strong>impacto emocional</strong>, a operação otimiza o que não
            importa e ignora o que mais dói.
          </p>

          <h2 id="por-que-importa">
            Por que medir o impacto emocional muda o jogo
          </h2>
          <p>
            Dois clientes podem abrir a mesma reclamação — "minha entrega
            atrasou" — e ter consequências completamente diferentes: um
            agradece o cupom e segue cliente por anos; o outro vai ao Reclame
            Aqui, conta para 12 amigos e nunca mais volta. A diferença não está
            no fato. Está em <strong>como ele se sentiu durante o
            problema</strong>: foi escutado? Teve que repetir o caso três
            vezes? Recebeu uma resposta de robô às 23h? Esse é o dado que
            prevê churn — e quase ninguém coleta.
          </p>
          <p>
            Medir emoção também muda quem decide. Quando o time de produto vê
            que <strong>74% dos clientes que reclamaram de cobrança
            descreveram a experiência como "humilhante"</strong>, a fila de
            priorização muda na mesma reunião. Número frio de tíquetes não
            move ninguém; emoção quantificada move.
          </p>

          <h2 id="o-que-medir">Os 3 sinais emocionais que você precisa medir</h2>

          <h3>1. Sentimento da conversa</h3>
          <p>
            Use IA para classificar cada atendimento em{" "}
            <strong>positivo, neutro ou negativo</strong> — e, idealmente, em
            emoções específicas (raiva, frustração, ansiedade, alívio,
            gratidão). Não precisa ser perfeito: precisa ser consistente o
            suficiente para mostrar tendência. Cruze com motivo do contato e
            você descobre coisas como "boletos geram 3× mais raiva que
            entregas" — informação que nenhum CSAT entrega.
          </p>

          <h3>2. Esforço percebido (CES)</h3>
          <p>
            Pergunte ao final do atendimento:{" "}
            <em>"De 1 a 7, quão fácil foi resolver seu problema hoje?"</em>.
            Esforço alto é o melhor preditor isolado de cancelamento — mais
            forte que NPS. Cliente que precisou repetir o caso, foi
            transferido três vezes ou ficou 40 minutos na espera marca CES
            baixo mesmo quando o problema foi resolvido. É aí que o impacto
            emocional acumula.
          </p>

          <h3>3. Intenção futura pós-reclamação</h3>
          <p>
            NPS aplicado <strong>depois</strong> da resolução, separado do NPS
            geral. Esse é o termômetro de recuperação: mostra se o
            atendimento conseguiu transformar uma experiência negativa em
            neutra (ou positiva) — ou se apenas adiou o cancelamento. Empresas
            maduras acompanham <em>NPS de recuperação</em> como KPI próprio,
            porque ele revela a qualidade real do processo de pós-reclamação.
          </p>

          <h2 id="como-coletar">Como coletar sem irritar quem já está irritado</h2>
          <p>
            Pesquisa longa depois de reclamação é castigo duplo. Três regras
            que funcionam na prática:
          </p>
          <ul>
            <li>
              <strong>Uma pergunta por vez.</strong> CES em uma mensagem, NPS
              em outra (24h depois). Nunca os dois juntos.
            </li>
            <li>
              <strong>Canal onde a conversa aconteceu.</strong> Se reclamou no
              WhatsApp, pesquise no WhatsApp. E-mail para quem reclamou por
              e-mail. Trocar de canal derruba a taxa de resposta pela metade.
            </li>
            <li>
              <strong>Campo aberto opcional.</strong> A nota dá o número; o
              comentário dá a causa. IA agrupa esses comentários em temas
              automaticamente e mostra padrões que nenhum dashboard de tíquete
              mostra.
            </li>
          </ul>

          <h2 id="transformar-em-processo">
            Da emoção medida à melhoria de processo
          </h2>
          <p>
            Dado emocional só vira valor quando entra no ciclo de melhoria.
            Estrutura simples que recomendamos:
          </p>
          <ul>
            <li>
              <strong>Categorize por causa raiz, não por sintoma.</strong>{" "}
              "Boleto errado" é sintoma. "Integração com ERP perde caracteres
              especiais" é causa. Reclamação só vira melhoria quando chega na
              causa.
            </li>
            <li>
              <strong>Priorize por volume × impacto emocional.</strong> Um
              motivo com 200 ocorrências e sentimento neutro pode esperar; um
              com 40 ocorrências e sentimento "humilhação" vai para o topo.
            </li>
            <li>
              <strong>Ritual semanal de 45 minutos</strong> com CX, produto e
              operação. Top 3 causas raízes, dono definido, prazo combinado.
              Sem isso, dado vira relatório morto.
            </li>
            <li>
              <strong>Feche o ciclo com o cliente.</strong> Quando a melhoria
              for ao ar, avise quem reclamou: <em>"Você nos contou sobre X
              em maio. A partir de hoje, isso não acontece mais. Obrigado."</em>{" "}
              É a mensagem que transforma detrator em promotor.
            </li>
          </ul>

          <h2 id="papel-da-tecnologia">
            O papel da tecnologia (sem cair no hype)
          </h2>
          <p>
            Para medir emoção em escala você precisa de três peças
            conversando entre si:
          </p>
          <ul>
            <li>
              <strong>Plataforma omnichannel</strong> que unifica WhatsApp,
              Instagram, e-mail, voz e webchat em um único histórico do
              cliente — sem isso o atendente não enxerga o contexto e o cliente
              repete o caso (CES despenca).
            </li>
            <li>
              <strong>IA aplicada a conversas</strong> para classificar
              sentimento, sumarizar atendimentos longos e sugerir respostas
              alinhadas ao tom de voz da marca — liberando o humano para o
              que exige empatia real.
            </li>
            <li>
              <strong>CRM e BI conectados</strong> para cruzar reclamação com
              receita, plano, tempo de casa e LTV. É aí que a diretoria
              entende que reclamação não é custo de operação — é sinal de
              receita em risco.
            </li>
          </ul>

          <h2 id="erros-comuns">3 erros comuns ao medir impacto emocional</h2>
          <ul>
            <li>
              <strong>Olhar só a média.</strong> Média esconde extremos.
              Acompanhe a cauda — % de atendimentos com sentimento muito
              negativo. É lá que mora o churn.
            </li>
            <li>
              <strong>Punir o atendente pela nota.</strong> Nota baixa quase
              sempre é problema de processo, não de pessoa. Quando o time
              sente que a nota vai contra ele, começa a manipular o
              encerramento — e o dado morre.
            </li>
            <li>
              <strong>Não fechar o ciclo.</strong> Coletar e não responder é
              pior que não coletar. O cliente que respondeu a pesquisa espera
              ver que ela serviu para algo.
            </li>
          </ul>

          <h2 id="conclusao">Conclusão</h2>
          <p>
            Reclamação é o sinal mais barato e mais rico que uma empresa
            recebe — desde que ela escute as duas camadas: o que aconteceu e
            como o cliente se sentiu. Medir <strong>impacto emocional</strong>{" "}
            transforma o atendimento de centro de custo em motor de melhoria
            contínua: o time aprende, o produto evolui, a marca fica mais
            confiável e o churn cai. <strong>Não é sobre evitar reclamação.
            É sobre tratá-la como o ativo estratégico que ela sempre foi.</strong>
          </p>
          <p>
            Quer ver como a Kentro ajuda empresas brasileiras a medir
            sentimento, esforço e NPS de recuperação em um único lugar —
            ligado direto ao processo de melhoria?{" "}
            <Link to="/gestao-de-atendimento" className="text-primary-glow hover:underline">
              Conheça a Gestão de Atendimento Kentro
            </Link>{" "}
            ou{" "}
            <Link to="/ouvidoria" className="text-primary-glow hover:underline">
              veja como funciona nosso Canal de Denúncias
            </Link>
            .
          </p>
        </div>
      </div>

      <section className="mt-12">
        <h2 className="text-2xl font-bold tracking-tight md:text-3xl">
          Perguntas frequentes
        </h2>
        <div className="mt-5 space-y-3">
          {FAQ.map((f) => (
            <details
              key={f.q}
              className="group rounded-xl border border-border bg-card/40 p-4 open:bg-card/60"
            >
              <summary className="cursor-pointer list-none font-semibold">
                {f.q}
                <span className="float-right text-muted-foreground group-open:hidden">
                  +
                </span>
                <span className="float-right hidden text-muted-foreground group-open:inline">
                  −
                </span>
              </summary>
              <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="mt-10 rounded-2xl border border-border bg-card/40 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Continue lendo
        </h2>
        <ul className="mt-3 space-y-1.5 text-sm">
          <li>
            <Link
              to="/fortalecendo-a-cultura-empresarial-atraves-da-gestao-de-atendimento"
              className="text-primary-glow hover:underline"
            >
              Fortalecendo a cultura empresarial através da gestão de atendimento →
            </Link>
          </li>
          <li>
            <Link to="/ouvidoria" className="text-primary-glow hover:underline">
              Canal de Denúncias Kentro →
            </Link>
          </li>
          <li>
            <Link to="/ia" className="text-primary-glow hover:underline">
              IA aplicada ao atendimento →
            </Link>
          </li>
          <li>
            <Link to="/blog" className="text-primary-glow hover:underline">
              Mais artigos no Kentro News →
            </Link>
          </li>
        </ul>
      </section>

      <section className="gradient-primary shadow-glow mt-12 rounded-2xl p-6 text-white md:p-8">
        <h2 className="text-xl font-bold md:text-2xl">
          Pronto para transformar reclamações em melhoria contínua?
        </h2>
        <p className="mt-2 text-white/85">
          Fale com um especialista da Kentro e veja como medir sentimento,
          esforço e NPS de recuperação em um único painel.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Link
            to="/contato"
            className="inline-flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-semibold text-[color:var(--primary)] hover:bg-white/90"
          >
            Solicitar demonstração
          </Link>
          <a
            href={KENTRO.whatsappComercialUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-md border border-white/30 bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/20"
          >
            <MessageCircle className="size-4" /> WhatsApp (38) 3220-8706
          </a>
        </div>
      </section>

      <div className="mt-12">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" /> Voltar ao blog
        </Link>
      </div>
    </article>
  );
}
